import { message, Modal } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/auth.context';
import { socketClient } from '../../utility/SocketClient';
import { VoiceCallWebSocketEvents } from '../../config/config';
import { AudioStreamer } from '../../lib/voice-calling/audio-streamer';
import VolMeterWorket from '../../lib/voice-calling/worklets/vol-meter';
import { audioContext, base64ToArrayBuffer } from '../../lib/voice-calling/utils';
import { AudioRecorder } from '../../lib/voice-calling/audio-recorder';

const RingtoneSound = ({ play }) => {
    const audioRef = React.useRef(null);

    React.useEffect(() => {
        if (play) {
            const tryPlay = () => {
                audioRef.current?.play().catch(() => {
                    const resumeAudio = () => {
                        audioRef.current?.play().catch(() => {});
                        document.removeEventListener('click', resumeAudio);
                        document.removeEventListener('keydown', resumeAudio);
                    };
                    document.addEventListener('click', resumeAudio, { once: true });
                    document.addEventListener('keydown', resumeAudio, { once: true });
                });
            };
            tryPlay();
        } else {
            audioRef.current?.pause();
        }
    }, [play]);

    return <audio ref={audioRef} src="/assets/audio/calling.mp3" loop />;
};

const waitForIceGatheringComplete = (peerConnection, timeoutMs = 1500) => {
    if (peerConnection.iceGatheringState === 'complete') {
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        const timeout = setTimeout(() => {
            resolve();
        }, timeoutMs);

        const checkState = () => {
            if (peerConnection.iceGatheringState === 'complete') {
                clearTimeout(timeout);
                peerConnection.removeEventListener('icegatheringstatechange', checkState);
                resolve();
            }
        };

        peerConnection.addEventListener('icegatheringstatechange', checkState);
    });
};

const UnifiedCallInterface = () => {
    const [callState, setCallState] = useState('idle');
    const [callData, setCallData] = useState(null);
    const audioStreamerRef = useRef(null);
    const audioRecorderRef = useRef(null);
    const [creatingAudioStreamer, setCreatingAudioStreamer] = useState(false);
    const [volume, setVolume] = useState(0);

    const [isMicrophoneEnabled, setIsMicrophoneEnabled] = useState(false);
    const [microphoneError, setMicrophoneError] = useState(null);
    const [playRingtone, setPlayRingtone] = useState(false);

    const { apiClient } = useAuth();
    const callTimerRef = useRef(null);
    const localStreamRef = useRef(null);
    const peerConnectionRef = useRef(null);
    const remoteAudioRef = useRef(null);
    const whatsappCallIdRef = useRef(null);
    useEffect(() => {
        const handleContactAudioStream = (data) => {
            if (data) {
                try {
                    audioStreamerRef.current?.addPCM16(base64ToArrayBuffer(data));
                } catch (err) {
                    console.log('error in binary string', err);
                    console.error(err, data);
                }
            }
        };

        const handleIncomingTransferRequest = (call) => {
            console.log('Incoming Call', call);
            setCallData(call);
            setPlayRingtone(true);
            setCallState('ringing_incoming');
        };

        const handleCallClosed = (callData) => {
            console.log('Call closed', callData);
            setCallState('idle');
            setCallData(null);
            setPlayRingtone(false);
            resetCall();
        };

        socketClient.on(VoiceCallWebSocketEvents.CONTACT.AUDIO_STREAM_PACKET, handleContactAudioStream);
        socketClient.on(VoiceCallWebSocketEvents.SESSION.HUMAN_TRANSFER_REQUEST, handleIncomingTransferRequest);
        socketClient.on(VoiceCallWebSocketEvents.CONTACT.CLOSED, handleCallClosed);
        socketClient.on(VoiceCallWebSocketEvents.SESSION.CLOSED, handleCallClosed);
        //Handle disconnection
        socketClient.on('disconnect', () => {
            if (audioRecorderRef.current) {
                audioRecorderRef.current.stop();
                audioRecorderRef.current = null;
            }
            if (audioStreamerRef.current) {
                audioStreamerRef.current.stop();
                audioStreamerRef.current = null;
            }
        });

        return () => {
            // socketClient.off('direct-calling', handleDirectCalling);
            // socketClient.off('whatsapp-call-connect', handleWhatsappCallConnect);
            // socketClient.off('whatsapp-call-terminated', handleWhatsappCallTerminated);
            socketClient.off(VoiceCallWebSocketEvents.CONTACT.AUDIO_STREAM_PACKET, handleContactAudioStream);
        };
    }, []);

    // ── Outbound WhatsApp call (business-initiated) ──────────────────────────────
    // ContactChat fires a `start-whatsapp-call` window event; this owns the WebRTC
    // peer, the SDP handshake (answer arrives over socket), audio, and hang-up.
    useEffect(() => {
        const requestCallPermission = async (contactId) => {
            try {
                await apiClient.post('/api/integrations/whatsapp/calling/permission', { contactId });
                message.success('Call-permission request sent. Try again once the contact taps Allow.');
            } catch (err) {
                message.error(err?.response?.data?.message || 'Failed to send permission request');
            }
        };

        const startWhatsappCall = async (detail) => {
            if (!detail?.contactId) return;
            if (peerConnectionRef.current) {
                message.warning('A call is already in progress');
                return;
            }
            try {
                setCallData({ platform: 'whatsapp', contact: { name: detail.name, phone: detail.phone } });
                setCallState('ringing_outgoing');
                setPlayRingtone(true);

                const localStream = await navigator.mediaDevices.getUserMedia({
                    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
                    video: false,
                });
                localStreamRef.current = localStream;
                setIsMicrophoneEnabled(true);

                // ponytail: public STUN only. Add a TURN server here if calls fail on
                // restrictive networks.
                const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
                peerConnectionRef.current = pc;
                localStream.getTracks().forEach((track) => pc.addTrack(track, localStream));

                pc.ontrack = (event) => {
                    if (remoteAudioRef.current) {
                        remoteAudioRef.current.srcObject = event.streams[0];
                        remoteAudioRef.current.play().catch(() => {});
                    }
                };
                pc.onconnectionstatechange = () => {
                    if (['failed', 'disconnected', 'closed'].includes(pc.connectionState)) {
                        resetCall();
                    }
                };

                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);
                // Send a complete offer (candidates included) — WhatsApp does not trickle.
                await waitForIceGatheringComplete(pc);

                const { data } = await apiClient.post('/api/integrations/whatsapp/calling/initiate', {
                    contactId: detail.contactId,
                    sdpOffer: pc.localDescription.sdp,
                });
                const callId = data?.data?.[0]?.callId;
                if (!callId) throw new Error('No call id returned');
                whatsappCallIdRef.current = callId;
            } catch (error) {
                const errorMsg = error?.response?.data?.message || error?.message || 'Failed to initiate call';
                resetCall();
                if (/call permission/i.test(errorMsg)) {
                    Modal.confirm({
                        title: 'Call permission needed',
                        content:
                            'This contact has not allowed WhatsApp calls yet. Send a call-permission request now? They must tap Allow before you can call.',
                        okText: 'Send request',
                        onOk: () => requestCallPermission(detail.contactId),
                    });
                } else {
                    message.error(errorMsg);
                }
            }
        };

        const handleWhatsappCallConnect = async (data) => {
            const pc = peerConnectionRef.current;
            if (!pc || !data?.session?.sdp) return;
            if (whatsappCallIdRef.current && data.callId !== whatsappCallIdRef.current) return;
            try {
                await pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp: data.session.sdp }));
                setPlayRingtone(false);
                setCallState('connected');
            } catch (e) {
                message.error('Failed to connect call audio');
                resetCall();
            }
        };

        const handleWhatsappCallTerminated = () => {
            if (peerConnectionRef.current || whatsappCallIdRef.current) {
                resetCall();
            }
        };

        const onStartEvent = (e) => startWhatsappCall(e.detail);

        window.addEventListener('start-whatsapp-call', onStartEvent);
        socketClient.on('whatsapp-call-connect', handleWhatsappCallConnect);
        socketClient.on('whatsapp-call-terminated', handleWhatsappCallTerminated);

        return () => {
            window.removeEventListener('start-whatsapp-call', onStartEvent);
            socketClient.off('whatsapp-call-connect', handleWhatsappCallConnect);
            socketClient.off('whatsapp-call-terminated', handleWhatsappCallTerminated);
        };
        // resetCall is a stable useCallback; intentionally omitted to avoid a TDZ on its
        // forward declaration.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiClient]);

    const acceptTransfer = async () => {
        console.log('accepting call', callData);

        if (!audioStreamerRef.current && !creatingAudioStreamer) {
            console.log('creating audio streamer');
            setCreatingAudioStreamer(true);
            const audioCtx = await audioContext({ id: 'audio-out' });
            audioStreamerRef.current = new AudioStreamer(audioCtx, {
                sampleRate: 16000,
            });
            await audioStreamerRef.current.addWorklet('vumeter-out', VolMeterWorket, (ev) => {
                setVolume(ev.data.volume);
            });
        } else {
            console.log('Audio stream context already exists', audioStreamerRef.current);
            setCreatingAudioStreamer(false);
        }
        try {
            if (!audioRecorderRef.current) {
                console.log('creating audio recorder');
                audioRecorderRef.current = new AudioRecorder();
                audioRecorderRef.current.start().then(() => {
                    console.log('audio recorder started', audioRecorderRef.current);
                    audioRecorderRef.current.on('data', (data) => {
                        socketClient.emit(VoiceCallWebSocketEvents.HUMAN_AGENT.AUDIO_STREAM_PACKET, data);
                    });
                });
            } else {
                audioRecorderRef.current.start().then(() => {
                    audioRecorderRef.current.on('data', (data) => {
                        socketClient.emit(VoiceCallWebSocketEvents.HUMAN_AGENT.AUDIO_STREAM_PACKET, data);
                    });
                });
            }

            setIsMicrophoneEnabled(true);
        } catch (micError) {
            console.error('Microphone access denied:', micError);
            message.warning("Microphone access denied - you won't be able to speak");
            setMicrophoneError('Microphone access denied');
        }
        setPlayRingtone(false);
        setCreatingAudioStreamer(false);
        if (callData) {
            await apiClient.post('/api/voice-calling/transfer/accept', {
                callId: callData.id,
            });
            setCallState('connected');
        }
    };

    const rejectTransfer = async () => {
        try {
            await apiClient.post('/api/voice-calling/transfer/reject', {
                callId: callData.id,
            });
            message.info('Call rejected');
            if (audioRecorderRef.current) {
                audioRecorderRef.current.stop();
                audioRecorderRef.current = null;
            }
            if (audioStreamerRef.current) {
                audioStreamerRef.current.stop();
                audioStreamerRef.current = null;
            }
        } catch (error) {
            console.error('Error rejecting call:', error);
        } finally {
            resetCall();
        }
    };

    // End call
    const endCall = async () => {
        try {
            // Outbound WhatsApp call uses its own terminate endpoint.
            if (callData?.platform === 'whatsapp' && whatsappCallIdRef.current) {
                await apiClient.post('/api/integrations/whatsapp/calling/terminate', {
                    callId: whatsappCallIdRef.current,
                });
            } else if (callData?.id) {
                await apiClient.post('/api/voice-calling/end-call', {
                    callId: callData.id,
                });
            }
        } catch (error) {
            console.error('Error ending call:', error);
        } finally {
            resetCall();
        }
    };

    // Toggle microphone
    const toggleMicrophone = useCallback(() => {
        if (!localStreamRef.current) {
            navigator.mediaDevices
                .getUserMedia({
                    audio: {
                        echoCancellation: true,
                        noiseSuppression: true,
                        autoGainControl: true,
                    },
                    video: false,
                })
                .then((stream) => {
                    localStreamRef.current = stream;
                    setIsMicrophoneEnabled(true);
                    setMicrophoneError(null);

                    if (peerConnectionRef.current) {
                        const senders = peerConnectionRef.current.getSenders();
                        stream.getTracks().forEach((track) => {
                            const existingSender = senders.find((s) => s.track?.kind === track.kind);
                            if (existingSender) {
                                existingSender.replaceTrack(track);
                            } else {
                                peerConnectionRef.current.addTrack(track, stream);
                            }
                        });
                    }
                    message.success('Microphone enabled');
                })
                .catch((error) => {
                    setMicrophoneError('Failed to access microphone');
                    message.error('Failed to access microphone');
                });
            return;
        }

        const audioTracks = localStreamRef.current.getTracks();
        const isCurrentlyEnabled = audioTracks[0]?.enabled;

        audioTracks.forEach((track) => {
            track.enabled = !isCurrentlyEnabled;
        });

        setIsMicrophoneEnabled(!isCurrentlyEnabled);
        message.info(!isCurrentlyEnabled ? 'Microphone enabled' : 'Microphone muted');
    }, []);

    // Reset call state
    const resetCall = useCallback(() => {
        // Stop ringtone
        setPlayRingtone(false);

        if (audioRecorderRef.current) {
            audioRecorderRef.current.stop();
            audioRecorderRef.current = null;
        }
        if (audioStreamerRef.current) {
            audioStreamerRef.current.stop();
            audioStreamerRef.current = null;
        }

        // WebRTC teardown (outbound WhatsApp call path)
        if (peerConnectionRef.current) {
            try {
                peerConnectionRef.current.close();
            } catch (e) {
                // ignore
            }
            peerConnectionRef.current = null;
        }
        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach((t) => t.stop());
            localStreamRef.current = null;
        }
        if (remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = null;
        }
        if (callTimerRef.current) {
            clearInterval(callTimerRef.current);
            callTimerRef.current = null;
        }
        whatsappCallIdRef.current = null;

        setCallState('idle');
        setCallData(null);
        setIsMicrophoneEnabled(false);
        setMicrophoneError(null);
    }, []);
    // Get display name
    const getDisplayName = () => {
        if (!callData) return 'Unknown';
        if (callData.contact?.name) return callData.contact.name;
        if (callData.contact?.phone) return callData.contact.phone;
        if (callData.callerNumber) return callData.callerNumber;
        return 'Guest';
    };

    // Don't render if no active call or if business call is still ringing
    if (callState === 'idle') return null;

    const isIncoming = callState === 'ringing_incoming';
    const isOutgoing = callState === 'ringing_outgoing';
    const isConnecting = callState === 'connecting';
    const isConnected = callState === 'connected';

    return (
        <>
            {/* Ringtone Component */}
            <RingtoneSound play={playRingtone} />

            {/* Hidden audio element for remote audio */}
            <audio ref={remoteAudioRef} autoPlay playsInline style={{ display: 'none' }} />

            {/* Call Interface */}
            <div style={{ position: 'fixed', top: '16px', right: '16px', zIndex: 10000 }}>
                <div
                    style={{
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(20px)',
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
                        padding: '16px 18px',
                        minWidth: '360px',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                    }}
                >
                    {/* Header */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '12px',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div
                                style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    background: isConnected
                                        ? 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)'
                                        : 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: isConnected
                                        ? '0 2px 8px rgba(82, 196, 26, 0.3)'
                                        : '0 2px 8px rgba(24, 144, 255, 0.3)',
                                    animation: isIncoming || isOutgoing ? 'pulse 2s ease-in-out infinite' : 'none',
                                }}
                            >
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="white"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                </svg>
                            </div>
                            <div>
                                <div
                                    style={{ fontSize: '14px', fontWeight: '600', color: '#262626', lineHeight: '1.2' }}
                                >
                                    {isIncoming && 'Incoming Call'}
                                    {isOutgoing && 'Calling...'}
                                    {isConnecting && 'Connecting...'}
                                    {isConnected && 'Active Call'}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                                    <span
                                        style={{
                                            width: '6px',
                                            height: '6px',
                                            borderRadius: '50%',
                                            background: isConnected ? '#52c41a' : '#1890ff',
                                            display: 'inline-block',
                                            animation: 'blink 2s ease-in-out infinite',
                                        }}
                                    />
                                    <span style={{ color: '#8c8c8c', fontSize: '12px' }}>
                                        {isIncoming && 'Ringing'}
                                        {isOutgoing && 'Ringing'}
                                        {isConnecting && 'Connecting'}
                                        {isConnected && 'Connected'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{ height: '1px', background: '#f0f0f0', margin: '12px 0' }} />

                    {/* Contact Info */}
                    <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontSize: '13px', color: '#595959', marginBottom: '4px' }}>
                            {getDisplayName()}
                        </div>
                    </div>

                    {/* Microphone Status (only when connected) */}
                    {isConnected && (
                        <>
                            <div style={{ marginBottom: '12px' }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        marginBottom: '8px',
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke={isMicrophoneEnabled ? '#52c41a' : '#d9d9d9'}
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                                            <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                                            <line x1="12" y1="19" x2="12" y2="23"></line>
                                            <line x1="8" y1="23" x2="16" y2="23"></line>
                                        </svg>
                                        <span style={{ fontSize: '13px', color: '#595959', fontWeight: '500' }}>
                                            Microphone
                                        </span>
                                    </div>
                                    <span
                                        style={{
                                            fontSize: '12px',
                                            color: isMicrophoneEnabled ? '#52c41a' : '#8c8c8c',
                                            fontWeight: '500',
                                        }}
                                    >
                                        {isMicrophoneEnabled ? 'ON' : 'OFF'}
                                    </span>
                                </div>

                                {isMicrophoneEnabled && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '3px',
                                            height: '24px',
                                            background: '#f6ffed',
                                            borderRadius: '6px',
                                            padding: '4px',
                                        }}
                                    >
                                        {[...Array(8)].map((_, i) => (
                                            <div
                                                key={i}
                                                style={{
                                                    width: '3px',
                                                    background: '#52c41a',
                                                    borderRadius: '2px',
                                                    animation: `audioBar 0.8s ease-in-out infinite ${i * 0.1}s`,
                                                }}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {isIncoming && (
                            <>
                                <button
                                    onClick={rejectTransfer}
                                    style={{
                                        flex: 1,
                                        background: '#ff4d4f',
                                        border: 'none',
                                        color: 'white',
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '6px',
                                    }}
                                >
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                        <line x1="23" y1="1" x2="1" y2="23"></line>
                                    </svg>
                                    Reject
                                </button>
                                <button
                                    onClick={acceptTransfer}
                                    style={{
                                        flex: 1,
                                        background: '#52c41a',
                                        border: 'none',
                                        color: 'white',
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '6px',
                                    }}
                                >
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                    </svg>
                                    Accept
                                </button>
                            </>
                        )}

                        {(isOutgoing || isConnecting) && (
                            <button
                                onClick={endCall}
                                style={{
                                    flex: 1,
                                    background: '#ff4d4f',
                                    border: 'none',
                                    color: 'white',
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px',
                                }}
                            >
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                </svg>
                                {isOutgoing ? 'Cancel' : 'End Call'}
                            </button>
                        )}

                        {isConnected && (
                            <>
                                <button
                                    onClick={toggleMicrophone}
                                    style={{
                                        flex: 1,
                                        background: 'white',
                                        border: '1px solid #d9d9d9',
                                        color: '#262626',
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '6px',
                                    }}
                                >
                                    {isMicrophoneEnabled ? (
                                        <>
                                            <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <line x1="1" y1="1" x2="23" y2="23"></line>
                                                <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
                                                <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path>
                                                <line x1="12" y1="19" x2="12" y2="23"></line>
                                                <line x1="8" y1="23" x2="16" y2="23"></line>
                                            </svg>
                                            Mute
                                        </>
                                    ) : (
                                        <>
                                            <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                                                <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                                                <line x1="12" y1="19" x2="12" y2="23"></line>
                                                <line x1="8" y1="23" x2="16" y2="23"></line>
                                            </svg>
                                            Unmute
                                        </>
                                    )}
                                </button>
                                <button
                                    onClick={endCall}
                                    style={{
                                        flex: 1,
                                        background: '#ff4d4f',
                                        border: 'none',
                                        color: 'white',
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '6px',
                                    }}
                                >
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                    </svg>
                                    End Call
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* CSS Keyframes */}
            <style>
                {`
                @keyframes blink {
                    0%, 100% {
                        opacity: 1;
                    }
                    50% {
                        opacity: 0.3;
                    }
                }

                @keyframes pulse {
                    0%, 100% {
                        transform: scale(1);
                        box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3);
                    }
                    50% {
                        transform: scale(1.05);
                        box-shadow: 0 4px 12px rgba(24, 144, 255, 0.5);
                    }
                }

                @keyframes audioBar {
                    0%, 100% {
                        height: 8px;
                    }
                    50% {
                        height: 16px;
                    }
                }
            `}
            </style>
        </>
    );
};

export default UnifiedCallInterface;
